import { useMemo, type RefObject } from 'react';
import * as d3 from 'd3';
import { VisualizationContainer } from 'tabview/ViewContainer';
import { useResizeObserver } from '@ardoq/hooks';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import type { FormatNodeValue, SizedNode } from '../../types';
import { buildHierarchy } from '../buildHierarchy';
import { HierarchicalPackingViewNode } from './HierarchicalPackingViewNode';
import { HierarchicalPackingViewLabel } from './HierarchicalPackingViewLabel';
import { t, type I18nLocale } from '@ardoq/i18n';
import { toAccessibleName, toNavigableNodes } from '../hierarchyNavigation';
import { useHierarchyNavigation } from '../useHierarchyNavigation';
import { toDrawnOrder } from '../toDrawnOrder';
import {
  MAX_BAND_SHARE_OF_RADIUS,
  measurerForSize,
  PACK_PADDING_YIELD,
  toGroupLabel,
  type GroupLabel,
} from '../svgLabels';

type Size = [width: number, height: number];

type CircularNode = d3.HierarchyCircularNode<SizedNode>;

/**
 * The gap the layout actually left between a circle's edge and its nearest child, which is what
 * its own name has to fit in. `d3.pack` insets children by less than the padding it is given —
 * two thirds of it at the depths a heading needs — so a label laid out against the padding that
 * was asked for is drawn over the children.
 */
const toChildGap = (node: CircularNode): number =>
  Math.min(
    ...(node.children ?? []).map(
      child =>
        node.r - (Math.hypot(child.x - node.x, child.y - node.y) + child.r)
    )
  );

const packWith = (
  size: Size,
  toPadding: (node: d3.HierarchyNode<SizedNode>) => number
) => d3.pack<SizedNode>().size(size).padding(toPadding);

/**
 * The depth whose groups are given a band deep enough for a heading.
 *
 * A band comes out of the children's area and the cost compounds, so giving every level one
 * halves the radius a leaf ends up with — the difference between a component that can be named
 * and a dot. The outermost groups are the ones a reader orients by, so they get the room and
 * everything below them keeps a plain line.
 */
const HEADING_DEPTH = 1;

/**
 * What every other group is inset by: enough to separate it from its children and no more.
 *
 * A band deep enough to name a group at this depth is not affordable — the chord near the rim
 * of a small circle holds a couple of characters, so buying one costs the children their own
 * names and buys the group nothing. Measured on a three-level model, going from 4px to 17px
 * here took the mean leaf radius from 12.2 to 9.9 and named no additional group.
 */
const BASE_PADDING = 4;

/**
 * What to inset a group by so its own heading has somewhere to go, or {@link BASE_PADDING} where
 * there will be no heading.
 *
 * `toGroupLabel` reports the whole gap it was offered on both of the paths where it gives up on a
 * name - a band under `MIN_LABEL_BAND`, and an empty label - and `lines` is the only thing that
 * says which happened. Taking `.band` unconditionally bought a heading for every depth-1 group
 * too small to carry one: at `r = 39` the band comes back as `0.3 * 39 = 11.7`, just under the 12
 * it takes to set one line, and dividing by `PACK_PADDING_YIELD` asks d3 for 16.7px of padding
 * against a base of 4. The children pack into `r ≈ 27` instead of `r ≈ 36` - two fifths of their
 * area, in the view whose whole point is reading magnitude off area, and nothing drawn in the
 * space it bought. An empty label reaches the same place at any radius, and `buildGroups` falls
 * back to `''` for a missing workspace name or component type.
 */
export const toHeadingPadding = (label: string | null, r: number): number => {
  const { band, lines } = toGroupLabel({
    label,
    r,
    maxBand: MAX_BAND_SHARE_OF_RADIUS * r,
    measurerFor: measurerForSize,
  });

  return lines.length ? band / PACK_PADDING_YIELD : BASE_PADDING;
};

/**
 * Packed twice, because how deep a band a name needs depends on the radius of the circle it goes
 * around, and that is only known once the layout has run. The first pass reserves a plain line
 * per group and reports what each circle came out at; the second asks for the band that group's
 * own name wants. What the label is finally set at is decided from the second pass's geometry,
 * so it fits the gap that exists rather than the one that was requested.
 *
 * Only laid out once the container has a size: `packEnclose` throws its `extendBasis` invariant
 * for `size([0, 0])` with any padding at all, and the first render is exactly that.
 */
/**
 * Whether the depth setting leaves any heading group with a child on screen.
 *
 * `toGroupLabels` labels a circle only where its children are drawn, so at a depth of one there
 * is no heading to draw - and unlike the treemap, where `paddingTop` is spent inside a rect
 * already fixed, `d3.pack` adds the padding to the group's own radius and takes the room from its
 * siblings. Reserving a band nobody writes in therefore draws two equally valued capabilities at
 * different sizes according to how long their names are, in the view read by area.
 */
const drawsHeadings = (maxDepth: number): boolean =>
  maxDepth <= 0 || maxDepth > HEADING_DEPTH;

export const pack = (
  size: Size,
  hierarchy: d3.HierarchyNode<SizedNode>,
  maxDepth: number
): CircularNode => {
  const measured = packWith(size, () => BASE_PADDING)(hierarchy);
  if (!drawsHeadings(maxDepth)) {
    return measured;
  }

  const reserved = measured
    .descendants()
    .filter(
      ({ children, depth }) =>
        Boolean(children?.length) && depth === HEADING_DEPTH
    )
    .map(({ data, r }): [string, number] => [
      data.id,
      toHeadingPadding(data.label, r),
    ])
    /* A node the accessor below would have answered with anyway, so keeping it says nothing. */
    .filter(([, padding]) => padding !== BASE_PADDING);

  /* Nothing left to ask for, so the second accessor is the first one and the pass would re-pack
     the tree to reproduce what it already has. Two ordinary shapes reach here: a hierarchy one
     level deep, where no node passes the filter at all, and one whose heading groups are all too
     small or too unnamed for `toHeadingPadding` to answer with anything else. */
  if (!reserved.length) {
    return measured;
  }

  const wanted = new Map(reserved);
  return packWith(
    size,
    ({ data }) => wanted.get(data.id) ?? BASE_PADDING
  )(hierarchy);
};

/**
 * A circle is a group only where its children are on screen. One whose children the depth
 * setting cut away is a leaf as far as a reader is concerned, and labelling it along its rim
 * rather than through its middle is what put headings on circles with nothing in them.
 */
const toGroupLabels = (
  descendants: CircularNode[]
): Map<string, GroupLabel> => {
  const drawn = new Set(descendants.map(({ data }) => data.id));
  return new Map(
    descendants
      .filter(({ children }) =>
        children?.some(child => drawn.has(child.data.id))
      )
      .map(node => [
        node.data.id,
        toGroupLabel({
          label: node.data.label,
          r: node.r,
          maxBand: toChildGap(node),
          measurerFor: measurerForSize,
        }),
      ])
  );
};

export type HierarchicalPackingVisualizationProps = {
  sizedNodes: SizedNode[];
  maxDepth: number;
  formatValue: FormatNodeValue;
  viewContainer: RefObject<HTMLDivElement | null>;
  i18nLocale: I18nLocale;
};

/**
 * The circle packing itself, with nothing about which mode is drawing it.
 *
 * Split out so each mode's view wraps it in its own shell. See
 * {@link HierarchicalTreemapVisualization} for why the split is here rather than at the pixel.
 */
export const HierarchicalPackingVisualization = ({
  sizedNodes,
  maxDepth,
  formatValue,
  viewContainer,
  i18nLocale,
}: HierarchicalPackingVisualizationProps) => {
  const { width = 0, height = 0 } = useResizeObserver(viewContainer);
  /* `hierarchy.value` alongside the size for the same reason: `d3.pack` rescales by `root.r` at
     the end, so a tree summing to nothing divides by zero and every circle comes back with a
     radius, an x and a y of `NaN`. A field the model declares and no component in scope carries
     a value for is enough to get there. */
  const packingRootNode = useMemo(() => {
    const hierarchy = buildHierarchy(sizedNodes);
    return width > 0 && height > 0 && (hierarchy.value ?? 0) > 0
      ? pack([width, height], hierarchy, maxDepth)
      : null;
  }, [sizedNodes, width, height, maxDepth]);
  /* Memoized for the reason `HierarchicalTreemapVisualization` gives: a fresh array every render
     makes `useHierarchyNavigation`'s memos dead and re-attaches every circle's ref. */
  const descendants = useMemo(
    () => (packingRootNode ? toDrawnOrder(packingRootNode, maxDepth) : []),
    [packingRootNode, maxDepth]
  );
  const groupLabels = toGroupLabels(descendants);

  const navigableNodes = useMemo(
    () => toNavigableNodes(descendants),
    [descendants]
  );
  const { treeProps, toNodeProps } = useHierarchyNavigation(
    navigableNodes,
    t(
      i18nLocale,
      'tabview:hierarchicalTreemap.packingTreeLabel',
      'Hierarchical circle packing'
    )
  );

  return (
    <VisualizationContainer
      data-context-menu={OBJECT_CONTEXT_MENU_NAME}
      ref={viewContainer}
    >
      <svg style={{ height: '100%', width: '100%' }} {...treeProps}>
        {descendants.map((node, index) => (
          <HierarchicalPackingViewNode
            key={node.data.id}
            node={node}
            a11yProps={toNodeProps(
              navigableNodes[index],
              toAccessibleName(node.data.label, formatValue(node.value))
            )}
          />
        ))}
        {/* Already spoken by each circle's `aria-label`, so hidden rather than read twice.
            On the group rather than per label, so a label the geometry declines to draw cannot
            be the one that was left exposed. */}
        <g aria-hidden="true" data-testid="packing-labels">
          {descendants.map(node => (
            <HierarchicalPackingViewLabel
              key={`text_${node.data.id}`}
              node={node}
              formatValue={formatValue}
              groupLabel={groupLabels.get(node.data.id) ?? null}
            />
          ))}
        </g>
      </svg>
    </VisualizationContainer>
  );
};
